import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { CalendarCheck, Check, Loader2, AlertCircle } from "lucide-react";
import Sparkle from "@/components/Sparkle";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function Shell({ children }) {
  return (
    <div className="grain flex min-h-screen items-center justify-center bg-espresso-deep px-5 py-16">
      <div
        data-testid="payment-card"
        className="w-full max-w-md rounded-3xl border border-cream/15 bg-espresso p-10 text-center text-cream"
      >
        {children}
      </div>
    </div>
  );
}

function HomeLink() {
  return (
    <Link
      to="/"
      data-testid="payment-home-link"
      className="mt-8 inline-block rounded-full border border-cream/40 px-8 py-3 text-sm font-semibold uppercase tracking-[0.16em] text-cream transition-colors hover:border-gold hover:text-gold"
    >
      Back to site
    </Link>
  );
}

export function PaymentSuccess() {
  const [status, setStatus] = useState("checking");
  const [amount, setAmount] = useState(null);
  const [booking] = useState(() => {
    try {
      return JSON.parse(sessionStorage.getItem("ss_booking") || "null");
    } catch {
      return null;
    }
  });
  const sessionId = new URLSearchParams(window.location.search).get("session_id");

  useEffect(() => {
    if (!sessionId) {
      setStatus("missing");
      return;
    }
    let tries = 0;
    let timer;
    const poll = async () => {
      tries += 1;
      try {
        const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setStatus("paid");
          if (data.amount) setAmount(data.amount);
          sessionStorage.removeItem("ss_booking");
          return;
        }
      } catch (e) {
        /* keep polling */
      }
      if (tries < 10) timer = setTimeout(poll, 2500);
      else setStatus("pending");
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  return (
    <Shell>
      {status === "checking" && (
        <>
          <Loader2 className="mx-auto h-10 w-10 animate-spin text-gold" />
          <p className="mt-6 font-display text-3xl">Confirming your payment...</p>
          <p className="mt-3 text-sm text-cream/60">This only takes a moment.</p>
        </>
      )}

      {status === "paid" && (
        <>
          <span data-testid="payment-success-icon" className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-gold text-espresso">
            <Check className="h-8 w-8" strokeWidth={3} />
          </span>
          <h1 className="mt-6 font-display text-4xl">
            Date <em className="italic text-rose">locked</em>!
          </h1>
          <p className="mt-4 text-sm leading-relaxed text-cream/70">
            {amount ? `Your $${amount} deposit is in` : "Your deposit is in"} and{" "}
            {booking?.venue ? `the booking for ${booking.venue}` : "your booking"} is
            marked first-priority. We'll confirm the details within 24 hours.
          </p>
          {booking?.reference && (
            <p className="mt-6 inline-block rounded-full border border-cream/20 px-6 py-2 text-sm tracking-[0.2em] text-gold" data-testid="payment-success-ref">
              REF: {booking.reference}
            </p>
          )}
          {booking?.date && (
            <p className="mt-4 flex items-center justify-center gap-2 text-sm text-cream/60">
              <CalendarCheck className="h-4 w-4 text-gold" /> {booking.date}
              {booking.guests ? ` · ${booking.guests} guests` : ""}
            </p>
          )}
          <HomeLink />
        </>
      )}

      {status === "pending" && (
        <>
          <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-cream/10 text-gold">
            <Loader2 className="h-7 w-7 animate-spin" />
          </span>
          <h1 className="mt-6 font-display text-3xl">Payment received</h1>
          <p className="mt-4 text-sm leading-relaxed text-cream/70" data-testid="payment-pending-note">
            We're confirming it with our processor — it can take a minute. We'll email you
            shortly to lock everything in.
          </p>
          <HomeLink />
        </>
      )}

      {status === "missing" && (
        <>
          <AlertCircle className="mx-auto h-10 w-10 text-gold" />
          <h1 className="mt-6 font-display text-3xl">Something's off</h1>
          <p className="mt-4 text-sm text-cream/70">
            We couldn't find that checkout session. If you completed a payment, email us and
            we'll sort it straight away.
          </p>
          <HomeLink />
        </>
      )}
    </Shell>
  );
}

export function PaymentCancel() {
  return (
    <Shell>
      <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-cream/10 text-gold">
        <Sparkle className="h-7 w-7" />
      </span>
      <h1 className="mt-6 font-display text-4xl">
        No <em className="italic text-rose">charge</em> made
      </h1>
      <p className="mt-4 text-sm leading-relaxed text-cream/70" data-testid="payment-cancel-note">
        Checkout was cancelled and your card wasn't touched — your booking request is still
        on file. Come back whenever you're ready to lock the date.
      </p>
      <HomeLink />
    </Shell>
  );
}